import { useState, useEffect } from "react";
import axios from "axios";
import { Search, Filter } from "lucide-react";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import CountryCard from "./CountryCard";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CountryList({ onSelectCountry }) {
  const [countries, setCountries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [currencyFilter, setCurrencyFilter] = useState("all");

  useEffect(() => {
    fetchCountries();
  }, []);

  const fetchCountries = async () => {
    try {
      const response = await axios.get(`${API}/countries/`);
      setCountries(response.data?.countries || []);
    } catch (error) {
      console.error("Error fetching countries:", error);
      setCountries([]);
    } finally {
      setLoading(false);
    }
  };

  const filteredCountries = countries.filter(country => {
    const matchesSearch = country.country_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         country.country_code.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCurrency = currencyFilter === "all" || country.currency === currencyFilter;
    return matchesSearch && matchesCurrency;
  });

  const uniqueCurrencies = [...new Set(countries.map(c => c.currency))];

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[40vh]">
        <div className="loading-spinner" data-testid="loading-spinner"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Compact Search and Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
          <Input
            placeholder="Search countries..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-8 h-9 text-sm"
            data-testid="country-search-input"
          />
        </div>
        <Select value={currencyFilter} onValueChange={setCurrencyFilter}>
          <SelectTrigger className="w-full sm:w-[140px] h-9" data-testid="currency-filter">
            <Filter className="w-3.5 h-3.5 mr-1.5" />
            <SelectValue placeholder="Currency" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Currencies</SelectItem>
            {uniqueCurrencies.map(currency => (
              <SelectItem key={currency} value={currency}>{currency}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Compact Countries Grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
        {filteredCountries.map((country, index) => (
          <CountryCard 
            key={country.country_code}
            country={country}
            onClick={() => onSelectCountry(country.country_code)}
            animationIndex={index}
          />
        ))}
      </div>

      {filteredCountries.length === 0 && (
        <div className="text-center py-8 text-zinc-500 text-sm">
          No countries found matching your criteria.
        </div>
      )}
    </div>
  );
}
